import data from '@emoji-mart/data';

/**
 * Convert a Slack emoji name to Unicode emoji using emoji-mart data
 * @param {string} name - Slack emoji name (with or without colons)
 * @returns {string} Unicode emoji or the original name with colons if not found
 */
export function slackEmojiToUnicode(name) {
  // Remove colons if present
  const cleanName = name.replace(/^:/, '').replace(/:$/, '');

  // Try to find the emoji in the emoji-mart data
  const emojis = data.emojis || {};

  // Direct lookup
  if (emojis[cleanName]) {
    return emojis[cleanName].skins[0].native;
  }

  // Try with common variations
  const variations = [
    cleanName,
    cleanName.replace(/_/g, '-'),
    cleanName.replace(/-/g, '_'),
    cleanName.replace(/_/g, ''),
    cleanName.replace(/-/g, '')
  ];

  for (const variant of variations) {
    if (emojis[variant]) {
      return emojis[variant].skins[0].native;
    }
  }

  // Check aliases
  for (const emojiId in emojis) {
    const emoji = emojis[emojiId];
    if (emoji.aliases && emoji.aliases.includes(cleanName)) {
      return emoji.skins[0].native;
    }
  }

  // Common manual mappings for Slack-specific names
  const slackSpecificMappings = {
    '+1': '👍',
    '-1': '👎',
    'thumbsup': '👍',
    'thumbsdown': '👎',
    'shipit': '🚀',
    'squirrel': '🐿️',
    'facepunch': '👊',
    'metal': '🤘',
    'fu': '🖕',
    'hankey': '💩',
    'poop': '💩',
    'shit': '💩',
    'the_horns': '🤘',
    'sign_of_the_horns': '🤘',
    'heavy_plus_sign': '➕',
    'heavy_minus_sign': '➖',
    'heavy_division_sign': '➗',
    'heavy_multiplication_x': '✖️',
    'heavy_dollar_sign': '💲',
    'heavy_check_mark': '✔️',
    'negative_squared_cross_mark': '❎',
    'white_check_mark': '✅',
    'ballot_box_with_check': '☑️',
    'radioactive': '☢️',
    'biohazard': '☣️',
    'orthodox_cross': '☦️',
    'star_and_crescent': '☪️',
    'peace_symbol': '☮️',
    'yin_yang': '☯️',
    'wheel_of_dharma': '☸️',
    'frowning_face': '☹️',
    'slightly_smiling_face': '🙂',
    'upside_down_face': '🙃',
    'money_mouth_face': '🤑',
    'nerd_face': '🤓',
    'hugging_face': '🤗',
    'zipper_mouth_face': '🤐',
    'thinking_face': '🤔',
    'face_with_rolling_eyes': '🙄',
    'drooling_face': '🤤',
    'lying_face': '🤧',
    'rofl': '🤣',
    'star_struck': '🤩',
    'zany_face': '🤪',
    'raised_eyebrow': '🤨',
    'shushing_face': '🤫',
    'hand_over_mouth': '🤭',
    'vomiting_face': '🤮',
    'exploding_head': '🤯',
    'pregnant_woman': '🤰',
    'breast_feeding': '🤱',
    'palms_up_together': '🤲',
    'selfie': '🤳',
    'prince': '🤴',
    'princess': '👸',
    'man_in_tuxedo': '🤵',
    'mrs_claus': '🤶',
    'shrug': '🤷',
    'person_facepalming': '🤦',
    'person_shrugging': '🤷',
    'face_palm': '🤦',
    'pregnant_man': '🫃',
    'breast_feeding': '🤱',
    'mx_claus': '🧑‍🎄',
    'superhero': '🦸',
    'supervillain': '🦹',
    'mage': '🧙',
    'fairy': '🧚',
    'vampire': '🧛',
    'merperson': '🧜',
    'elf': '🧝',
    'genie': '🧞',
    'zombie': '🧟'
  };

  if (slackSpecificMappings[cleanName]) {
    return slackSpecificMappings[cleanName];
  }

  // Return with colons if not found
  return `:${cleanName}:`;
}

/**
 * Check if a string is already a Unicode emoji
 * @param {string} str - String to check
 * @returns {boolean} True if the string is a Unicode emoji
 */
export function isUnicodeEmoji(str) {
  // Comprehensive emoji regex pattern
  const emojiRegex = /^(\p{Emoji_Presentation}|\p{Emoji}\uFE0F|\p{Emoji_Component})+$/u;
  return emojiRegex.test(str);
}

/**
 * Convert emoji to display format
 * @param {string} emoji - Emoji name or Unicode emoji
 * @returns {string} Unicode emoji for display
 */
export function getEmojiDisplay(emoji) {
  // If it's already a Unicode emoji, return as is
  if (isUnicodeEmoji(emoji)) {
    return emoji;
  }

  // Otherwise, convert from Slack name
  return slackEmojiToUnicode(emoji);
}

/**
 * Get emoji data from emoji-mart
 * @param {string} emojiId - Emoji ID from emoji-mart
 * @returns {object|null} Emoji data object or null if not found
 */
export function getEmojiData(emojiId) {
  const emojis = data.emojis || {};
  return emojis[emojiId] || null;
}